import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Pencil, FileText, Download, Trash2, Check, X as XIcon, Send, Mail, MessageSquare, Settings2, Layers, Clock, AlertTriangle, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { getQuotations, createQuotation, updateQuotation, deleteQuotation, downloadQuotationPdf, getFirms, getQuotation, sendQuotation, getMyCustomers, getMyProducts, requestQuotationDelete, getDeleteRequests, approveDeleteRequest, rejectDeleteRequest } from '../../services/taskApi';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  sent: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  converted: 'bg-green-500/10 text-green-400 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20'
};

const emptyItem = { product_name: '', description: '', hsn_code: '', make: '', quantity: 1, unit: 'Nos', rate: 0, list_price: 0, net_price: 0, discount_percent: 0, amount: 0, price_type: 'net' };
const emptyOperation = { op_number: '', operation_name: '', machine_name: '', items: [{ ...emptyItem }] };

const calcItemAmount = (item) => {
  const pt = item.price_type || 'net';
  const r = pt === 'list' ? (item.list_price || 0) * (1 - (item.discount_percent || 0) / 100) : (item.net_price || item.rate || 0);
  return (item.quantity || 0) * r;
};

const NumField = ({ value, field, placeholder, extraCls = '', onUpdate, index, isDark }) => (
  <input
    type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*"
    value={value} onChange={e => onUpdate(index, field, e.target.value)}
    placeholder={placeholder}
    className={`w-full h-9 rounded-lg text-sm px-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 border ${
      isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
    } ${extraCls}`}
  />
);

const ColHeaders = ({ isDark }) => (
  <div className={`hidden md:flex items-center gap-2 px-4 py-2 border-b text-[9px] font-semibold uppercase tracking-wider ${
    isDark ? 'bg-white/[0.03] border-white/5 text-white/25' : 'bg-gray-50 border-gray-200 text-gray-400'
  }`}>
    <div className="w-7">#</div><div className="flex-1 min-w-[100px]">Product</div><div className="w-[70px]">Make</div><div className="w-[70px]">HSN</div><div className="w-[60px] text-center">Qty</div><div className="w-[50px] text-center">Unit</div><div className="w-[60px]">Type</div><div className="w-[90px] text-right">Rate / Price</div><div className="w-[60px] text-center">Disc %</div><div className="w-[90px] text-right">Amount</div><div className="w-7"></div>
  </div>
);

const ItemRow = ({ item, index, onUpdate, onRemove, canRemove, listId, products, isDark }) => {
  const isListPrice = (item.price_type || 'net') === 'list';
  const amt = calcItemAmount(item);
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const selectCls = isDark ? 'bg-white/5 border border-white/10 text-white dark-select' : 'bg-white border border-gray-300 text-gray-900';
  return (
    <div className={`px-3 md:px-4 py-2.5 transition-colors group ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`} data-testid={`item-row-${listId}-${index}`}>
      {/* Desktop */}
      <div className="hidden md:flex items-center gap-2">
        <div className={`w-7 text-xs font-mono text-center ${isDark ? 'text-white/25' : 'text-gray-400'}`}>{index + 1}</div>
        <div className="flex-1 min-w-[100px]">
          <Input value={item.product_name} onChange={e => onUpdate(index, 'product_name', e.target.value)} placeholder="Product *" className={`${inputCls} h-9 rounded-lg text-sm`} list={`pl-${listId}-${index}`} />
          <datalist id={`pl-${listId}-${index}`}>{products.map((p,j) => <option key={j} value={p.product_name} />)}</datalist>
        </div>
        <div className="w-[70px]"><Input value={item.make || ''} onChange={e => onUpdate(index, 'make', e.target.value)} placeholder="Make" className={`${inputCls} h-9 rounded-lg text-xs`} /></div>
        <div className="w-[70px]"><Input value={item.hsn_code} onChange={e => onUpdate(index, 'hsn_code', e.target.value)} placeholder="HSN" className={`${inputCls} h-9 rounded-lg text-xs`} /></div>
        <div className="w-[60px]"><NumField value={item.quantity} field="quantity" placeholder="Qty" extraCls="text-center" onUpdate={onUpdate} index={index} isDark={isDark} /></div>
        <div className="w-[50px]"><Input value={item.unit} onChange={e => onUpdate(index, 'unit', e.target.value)} className={`${inputCls} h-9 rounded-lg text-xs text-center`} /></div>
        <div className="w-[60px]">
          <select className={`w-full h-9 rounded-lg text-[11px] px-1.5 ${selectCls}`} value={item.price_type || 'net'} onChange={e => onUpdate(index, 'price_type', e.target.value)}>
            <option value="net">Net</option><option value="list">List</option>
          </select>
        </div>
        <div className="w-[90px]">
          {isListPrice
            ? <NumField value={item.list_price} field="list_price" placeholder="List Price" extraCls="text-right" onUpdate={onUpdate} index={index} isDark={isDark} />
            : <NumField value={item.net_price || item.rate} field="net_price" placeholder="Net Price" extraCls="text-right" onUpdate={onUpdate} index={index} isDark={isDark} />}
        </div>
        <div className="w-[60px]">
          {isListPrice
            ? <NumField value={item.discount_percent} field="discount_percent" placeholder="%" extraCls="text-center bg-amber-500/5 !border-amber-500/20 !text-amber-300" onUpdate={onUpdate} index={index} isDark={isDark} />
            : <div className={`h-9 flex items-center justify-center text-xs ${isDark ? 'text-white/10' : 'text-gray-300'}`}>--</div>}
        </div>
        <div className={`w-[90px] text-right text-sm font-mono font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{Math.round(amt).toLocaleString('en-IN')}</div>
        <div className="w-7">{canRemove && <button type="button" onClick={() => onRemove(index)} className="text-red-400/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><XIcon size={14} /></button>}</div>
      </div>
      {/* Mobile */}
      <div className="md:hidden space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono ${isDark ? 'text-white/25' : 'text-gray-400'}`}>#{index+1}</span>
            <select className={`h-7 rounded text-xs px-1.5 ${selectCls}`} value={item.price_type || 'net'} onChange={e => onUpdate(index, 'price_type', e.target.value)}>
              <option value="net">Net</option><option value="list">List</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-mono ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{Math.round(amt).toLocaleString('en-IN')}</span>
            {canRemove && <button type="button" onClick={() => onRemove(index)} className="text-red-400/60 p-1"><XIcon size={14} /></button>}
          </div>
        </div>
        <Input value={item.product_name} onChange={e => onUpdate(index, 'product_name', e.target.value)} placeholder="Product *" className={`${inputCls} h-10 rounded-lg`} list={`plm-${listId}-${index}`} />
        <datalist id={`plm-${listId}-${index}`}>{products.map((p,j) => <option key={j} value={p.product_name} />)}</datalist>
        <div className="grid grid-cols-5 gap-2">
          <div><label className={`text-[9px] mb-0.5 block ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Make</label><Input value={item.make || ''} onChange={e => onUpdate(index, 'make', e.target.value)} className={`${inputCls} h-9 rounded-lg text-xs`} /></div>
          <div><label className={`text-[9px] mb-0.5 block ${isDark ? 'text-white/30' : 'text-gray-400'}`}>HSN</label><Input value={item.hsn_code} onChange={e => onUpdate(index, 'hsn_code', e.target.value)} className={`${inputCls} h-9 rounded-lg text-xs`} /></div>
          <div><label className={`text-[9px] mb-0.5 block ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Qty</label><NumField value={item.quantity} field="quantity" placeholder="Qty" extraCls="text-center" onUpdate={onUpdate} index={index} isDark={isDark} /></div>
          <div><label className={`text-[9px] mb-0.5 block ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Unit</label><Input value={item.unit} onChange={e => onUpdate(index, 'unit', e.target.value)} className={`${inputCls} h-9 rounded-lg text-xs text-center`} /></div>
          <div><label className={`text-[9px] mb-0.5 block ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{isListPrice ? 'List Price' : 'Net Price'}</label>
            {isListPrice ? <NumField value={item.list_price} field="list_price" placeholder="Price" extraCls="text-right" onUpdate={onUpdate} index={index} isDark={isDark} />
              : <NumField value={item.net_price || item.rate} field="net_price" placeholder="Price" extraCls="text-right" onUpdate={onUpdate} index={index} isDark={isDark} />}
          </div>
        </div>
        {isListPrice && (
          <div className="flex items-center gap-2">
            <label className="text-[9px] text-amber-400/60">Discount %</label>
            <NumField value={item.discount_percent} field="discount_percent" placeholder="%" extraCls="text-center bg-amber-500/5 !border-amber-500/20 !text-amber-300 w-20" onUpdate={onUpdate} index={index} isDark={isDark} />
          </div>
        )}
      </div>
    </div>
  );
};

const Quotations = () => {
  const { user, isDark } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [quotations, setQuotations] = useState([]);
  const [firms, setFirms] = useState([]);
  const [myCustomers, setMyCustomers] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendDialogId, setSendDialogId] = useState(null);
  const [sendOpts, setSendOpts] = useState({ email: true, whatsapp: true, customer_email: '', customer_phone: '' });
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteRequests, setDeleteRequests] = useState([]);
  const [deleteReqDialog, setDeleteReqDialog] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [viewQuotation, setViewQuotation] = useState(null);
  const [loadingView, setLoadingView] = useState(false);

  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const dialogCls = isDark ? 'bg-[#0c1222] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900';
  const cardBorderCls = isDark ? 'border border-white/5 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/10' : 'border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300';
  const sectionCls = isDark ? 'bg-white/[0.02] border border-white/8' : 'bg-gray-50 border border-gray-200';
  const labelCls = isDark ? 'text-[11px] text-white/50 uppercase tracking-wide' : 'text-[11px] text-gray-500 uppercase tracking-wide';
  const labelSecCls = isDark ? 'text-[11px] text-white/40' : 'text-[11px] text-gray-500';
  const btnOutlineCls = isDark ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100';

  const defaultForm = {
    quotation_type: 'general',
    firm_id: '', customer_name: '', customer_email: '', customer_phone: '',
    customer_company: '', customer_address: '', customer_gst: '',
    contact_person_name: '', contact_person_designation: '', contact_person_mobile: '', contact_person_email: '',
    component_name: '', component_material: '',
    items: [{ ...emptyItem }],
    operations: [{ ...emptyOperation, op_number: 'OP10' }],
    cgst_rate: 9, sgst_rate: 9, igst_rate: 0,
    notes: '', terms: 'Delivery: Ex-Works\nPayment: 100% Advance\nValidity: 15 Days', valid_until: '', status: 'draft'
  };

  const [form, setForm] = useState({ ...defaultForm });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.quotation_type = filterType;
      const promises = [
        getQuotations(params), getFirms(), getMyCustomers(), getMyProducts()
      ];
      if (isAdmin) promises.push(getDeleteRequests());
      const results = await Promise.all(promises);
      setQuotations(results[0].data || []);
      setFirms(results[1].data || []);
      setMyCustomers(results[2].data || []);
      setMyProducts(results[3].data || []);
      if (isAdmin && results[4]) setDeleteRequests(results[4].data || []);
    } catch (err) { console.error('Quotations: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calcTotals = () => {
    let allItems = [];
    if (form.quotation_type === 'project') {
      form.operations.forEach(op => { allItems.push(...(op.items || [])); });
    } else {
      allItems = form.items;
    }
    const subtotal = allItems.reduce((s, i) => s + calcItemAmount(i), 0);
    const cgst_amount = form.igst_rate > 0 ? 0 : subtotal * (form.cgst_rate / 100);
    const sgst_amount = form.igst_rate > 0 ? 0 : subtotal * (form.sgst_rate / 100);
    const igst_amount = form.igst_rate > 0 ? subtotal * (form.igst_rate / 100) : 0;
    const total = subtotal + cgst_amount + sgst_amount + igst_amount;
    return { subtotal, cgst_amount, sgst_amount, igst_amount, total };
  };

  const computed = calcTotals();

  const openCreate = (type = 'general') => {
    setEditing(null);
    setForm({
      ...defaultForm, quotation_type: type,
      firm_id: firms[0]?.id || '',
      contact_person_name: user.name || '', contact_person_designation: user.designation || '',
      contact_person_mobile: user.phone || '', contact_person_email: user.email || '',
    });
    setDialogOpen(true);
  };

  const handleView = async (q) => {
    setLoadingView(true);
    try {
      const res = await getQuotation(q.id);
      setViewQuotation(res.data);
    } catch (err) {
      console.error('Quotations: view failed', err);
      toast.error('Failed to load quotation details');
    } finally {
      setLoadingView(false);
    }
  };

  const openEdit = async (q) => {
    try {
      const res = await getQuotation(q.id);
      const d = res.data;
      setEditing(d);
      const isProject = d.quotation_type === 'project';
      setForm({
        ...defaultForm, ...d,
        quotation_type: d.quotation_type || 'general',
        contact_person_name: d.contact_person_name || d.sales_engineer_name || '',
        contact_person_designation: d.contact_person_designation || '',
        contact_person_mobile: d.contact_person_mobile || '',
        contact_person_email: d.contact_person_email || d.sales_engineer_email || '',
        items: isProject ? [{ ...emptyItem }] : (d.items?.length ? d.items.map(it => ({ ...emptyItem, ...it, price_type: it.price_type || 'net' })) : [{ ...emptyItem }]),
        operations: isProject && d.operations?.length ? d.operations.map(op => ({
          ...emptyOperation, ...op,
          items: op.items?.length ? op.items.map(it => ({ ...emptyItem, ...it, price_type: it.price_type || 'net' })) : [{ ...emptyItem }]
        })) : [{ ...emptyOperation, op_number: 'OP10' }],
        cgst_rate: d.cgst_rate ?? 9, sgst_rate: d.sgst_rate ?? 9, igst_rate: d.igst_rate ?? 0,
      });
      setDialogOpen(true);
    } catch { toast.error('Failed'); }
  };

  const selectCustomer = (name) => {
    const c = myCustomers.find(x => x.name === name);
    if (c) {
      setForm(f => ({ ...f, customer_name: c.name, customer_company: c.company || f.customer_company,
        customer_email: c.email || f.customer_email, customer_phone: c.phone || f.customer_phone,
        customer_address: c.address || f.customer_address, customer_gst: c.gst || f.customer_gst }));
    }
  };

  const selectProduct = (item, name) => {
    const p = myProducts.find(x => x.product_name === name);
    if (p) return { ...item, product_name: p.product_name, hsn_code: p.hsn_code || item.hsn_code, unit: p.unit || item.unit, rate: p.rate || 0, list_price: p.list_price || 0, net_price: p.net_price || 0 };
    return item;
  };

  const updateItem = (i, field, val) => {
    const items = [...form.items];
    const numFields = ['quantity', 'rate', 'list_price', 'net_price', 'discount_percent'];
    items[i] = { ...items[i], [field]: numFields.includes(field) ? parseFloat(val) || 0 : val };
    setForm(f => ({ ...f, items }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const addOperation = () => {
    const nextNum = (form.operations.length + 1) * 10;
    setForm(f => ({ ...f, operations: [...f.operations, { ...emptyOperation, op_number: `OP${nextNum}` }] }));
  };
  const removeOperation = (oi) => setForm(f => ({ ...f, operations: f.operations.filter((_, idx) => idx !== oi) }));
  const updateOperation = (oi, field, val) => {
    const ops = [...form.operations];
    ops[oi] = { ...ops[oi], [field]: val };
    setForm(f => ({ ...f, operations: ops }));
  };
  const addOpItem = (oi) => {
    const ops = [...form.operations];
    ops[oi] = { ...ops[oi], items: [...ops[oi].items, { ...emptyItem }] };
    setForm(f => ({ ...f, operations: ops }));
  };
  const removeOpItem = (oi, ii) => {
    const ops = [...form.operations];
    ops[oi] = { ...ops[oi], items: ops[oi].items.filter((_, idx) => idx !== ii) };
    setForm(f => ({ ...f, operations: ops }));
  };
  const updateOpItem = (oi, ii, field, val) => {
    const ops = [...form.operations];
    const numFields = ['quantity', 'rate', 'list_price', 'net_price', 'discount_percent'];
    const items = [...ops[oi].items];
    items[ii] = { ...items[ii], [field]: numFields.includes(field) ? parseFloat(val) || 0 : val };
    ops[oi] = { ...ops[oi], items };
    setForm(f => ({ ...f, operations: ops }));
  };
  const onProductSelectOp = (oi, ii, name) => {
    const ops = [...form.operations];
    const items = [...ops[oi].items];
    items[ii] = selectProduct(items[ii], name);
    ops[oi] = { ...ops[oi], items };
    setForm(f => ({ ...f, operations: ops }));
  };

  const isQuotationDirty = () => {
    if (form.customer_name?.trim() || form.customer_company?.trim() || form.customer_email?.trim() || form.customer_phone?.trim()) return true;
    if (form.component_name?.trim() || form.component_material?.trim() || form.notes?.trim()) return true;
    if (form.quotation_type === 'project') {
      return form.operations.some(op => op.operation_name?.trim() || op.machine_name?.trim() || (op.items || []).some(it => it.product_name?.trim() || parseFloat(it.net_price || 0) > 0 || parseFloat(it.list_price || 0) > 0));
    }
    return form.items.some(it => it.product_name?.trim() || parseFloat(it.net_price || 0) > 0 || parseFloat(it.list_price || 0) > 0);
  };

  const requestCloseDialog = () => {
    if (isQuotationDirty() && !window.confirm('Any unsaved data will be lost. Are you sure you want to close this quotation?')) return;
    setDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (!form.customer_name?.trim()) {
      toast.error('Customer name is required');
      return;
    }
    const allItems = form.quotation_type === 'project'
      ? form.operations.flatMap(op => op.items || [])
      : form.items;
    if (!allItems.length) {
      toast.error('At least one item is required');
      return;
    }
    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i];
      if (!item.product_name?.trim()) {
        toast.error(`Item ${i + 1}: Product name is required`);
        return;
      }
      const pt = item.price_type || 'net';
      const rate = pt === 'list'
        ? parseFloat(item.list_price || 0)
        : parseFloat(item.net_price || item.rate || 0);
      const qty = parseFloat(item.quantity || 0);
      if (rate <= 0) {
        toast.error(`Item ${i + 1}: Price must be greater than 0`);
        return;
      }
      if (qty <= 0) {
        toast.error(`Item ${i + 1}: Quantity must be greater than 0`);
        return;
      }
    }

    const { subtotal, cgst_amount, sgst_amount, igst_amount, total } = calcTotals();
    const data = { ...form, subtotal, cgst_amount, sgst_amount, igst_amount, total };
    if (form.quotation_type === 'project') {
      data.operations = form.operations.map(op => ({
        ...op,
        items: op.items.map(i => ({ ...i, amount: calcItemAmount(i) }))
      }));
      delete data.items;
    } else {
      data.items = form.items.map(i => ({ ...i, amount: calcItemAmount(i) }));
      delete data.operations;
      delete data.component_name;
      delete data.component_material;
    }
    try {
      setSubmitting(true);
      if (editing) { await updateQuotation(editing.id, data); toast.success('Updated'); }
      else { await createQuotation(data); toast.success('Created'); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDownloadPdf = async (id, quotation) => {
    try {
      const res = await downloadQuotationPdf(id);
      // Prefer filename from server's Content-Disposition (e.g., QT-26-0043_Ravi.pdf)
      let filename = '';
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m) filename = decodeURIComponent(m[1]);
      }
      if (!filename) {
        const qno = quotation?.quotation_number || 'quotation';
        const cust = (quotation?.customer_name || quotation?.client_name || '').split(' ')[0]?.replace(/[^A-Za-z0-9_-]/g, '') || '';
        filename = cust ? `${qno}_${cust}.pdf` : `${qno}.pdf`;
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Quotation PDF download failed', err); toast.error('PDF failed'); }
  };
  const handleSendQuotation = async () => {
    if (!sendDialogId) return;
    if (sendOpts.email && !sendOpts.customer_email?.trim()) {
      toast.error('Please enter customer email address');
      return;
    }
    if (sendOpts.whatsapp && !sendOpts.customer_phone?.trim()) {
      toast.error('Please enter customer phone number');
      return;
    }
    try {
      const res = await sendQuotation(sendDialogId, sendOpts);
      const r = res.data.results;
      if (r.email) toast.success('Email sent successfully!');
      else if (sendOpts.email && r.email_error) toast.error(r.email_error);
      if (r.whatsapp) toast.success('WhatsApp sent!');
      else if (sendOpts.whatsapp && r.whatsapp_error) toast.error(r.whatsapp_error);
      setSendDialogId(null); fetchData();
    } catch { toast.error('Send failed'); }
  };
  const handleStatusChange = async (id, s) => {
    try { await updateQuotation(id, { status: s }); toast.success('Updated'); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleDelete = async (q) => {
    // Check 48hr rule for non-admin
    if (!isAdmin) {
      const created = new Date(q.created_at);
      const hoursDiff = (Date.now() - created.getTime()) / (1000 * 60 * 60);
      if (hoursDiff > 48) {
        // Show delete request dialog instead
        setDeleteReqDialog(q);
        setDeleteReason('');
        return;
      }
    }
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await deleteQuotation(q.id);
      toast.success('Quotation deleted');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('48 hours')) {
        setDeleteReqDialog(q);
        setDeleteReason('');
      } else {
        toast.error(detail || 'Delete failed');
      }
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteReqDialog) return;
    try {
      await requestQuotationDelete(deleteReqDialog.id, deleteReason);
      toast.success('Delete request sent to admin');
      setDeleteReqDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    }
  };

  const handleApproveDelete = async (reqId) => {
    if (!window.confirm("Approve this delete request? The quotation will be permanently removed and cannot be recovered. Continue?")) return;
    try {
      await approveDeleteRequest(reqId);
      toast.success('Quotation deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleRejectDelete = async (reqId) => {
    if (!window.confirm("Reject this delete request? The quotation will be kept and the requester will see the rejection.")) return;
    try {
      await rejectDeleteRequest(reqId);
      toast.success('Request rejected');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Quotations</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className={`h-9 rounded-lg text-xs px-3 border w-48 ${inputCls}`}
            placeholder="Search customer or creator..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="search-quotation"
          />
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="filter-type">
            <option value="">All Types</option>
            <option value="general">General</option>
            <option value="project">Project</option>
          </select>
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
          <Button size="sm" onClick={() => openCreate('general')} className="bg-blue-600 hover:bg-blue-700 h-9 px-4" data-testid="create-quotation-btn"><FileText size={14} className="mr-1.5" /> General</Button>
          <Button size="sm" onClick={() => openCreate('project')} className="bg-indigo-600 hover:bg-indigo-700 h-9 px-4" data-testid="create-project-quotation-btn"><Layers size={14} className="mr-1.5" /> Project</Button>
        </div>
      </div>

      {loading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`rounded-xl h-24 animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div> : (() => {
        const filtered = quotations.filter(q => {
          if (!searchQuery.trim()) return true;
          const s = searchQuery.toLowerCase();
          return (q.customer_name || '').toLowerCase().includes(s) ||
            (q.customer_company || '').toLowerCase().includes(s) ||
            (q.contact_person_name || '').toLowerCase().includes(s) ||
            (q.created_by_name || '').toLowerCase().includes(s) ||
            (q.quotation_number || '').toLowerCase().includes(s);
        });
        return filtered.length === 0 ? (
        <div className={`text-center py-20 ${isDark ? 'text-white/30' : 'text-gray-400'}`}><FileText size={48} className="mx-auto mb-4 opacity-20" /><p className="text-lg">No quotations yet</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(q => (
            <Card key={q.id} className={`${cardBorderCls} transition-all`} data-testid={`quotation-card-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono font-bold text-blue-400">{q.quotation_number}</span>
                      <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-medium capitalize border ${STATUS_COLORS[q.status]}`}>{q.status}</span>
                      {q.quotation_type === 'project' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">Project</span>}
                      {q.firm_name && <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-100 text-gray-500'}`}>{q.firm_name}</span>}
                    </div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-gray-800'}`}>{q.customer_name} {q.customer_company ? `- ${q.customer_company}` : ''}</p>
                    <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                      <span className={`font-semibold ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Rs. {Math.round(q.total || 0).toLocaleString('en-IN')}</span>
                      {q.quotation_type === 'project' && q.component_name && <span>Comp: {q.component_name}</span>}
                      <span>{q.created_at?.split('T')[0]}</span>
                      {(q.contact_person_name || q.sales_engineer_name) && <span>By: {q.contact_person_name || q.sales_engineer_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 rounded-lg ${isDark ? 'text-cyan-400 hover:bg-cyan-500/10' : 'text-cyan-600 hover:bg-cyan-50'}`} onClick={() => handleView(q)} data-testid={`view-quotation-${q.id}`} title="View details"><Eye size={14} /></Button>
                    {q.status === 'draft' && <Button size="sm" variant="ghost" className="text-blue-400 hover:bg-blue-500/10 h-8 px-3 text-xs rounded-lg" onClick={() => handleStatusChange(q.id, 'sent')}>Mark Sent</Button>}
                    {q.status === 'sent' && <Button size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-500/10 h-8 px-3 text-xs rounded-lg" onClick={() => handleStatusChange(q.id, 'approved')}>Approve</Button>}
                    {(q.status === 'approved' || q.status === 'sent') && <Button size="sm" variant="ghost" className="text-green-400 hover:bg-green-500/10 h-8 px-3 text-xs rounded-lg" onClick={() => handleStatusChange(q.id, 'converted')}><Check size={12} className="mr-1" />Convert</Button>}
                    <Button size="sm" variant="ghost" className="text-indigo-400 hover:bg-indigo-500/10 h-8 px-3 text-xs rounded-lg" onClick={() => { setSendDialogId(q.id); setSendOpts({ email: true, whatsapp: true, customer_email: q.customer_email || '', customer_phone: q.customer_phone || '' }); }}><Send size={12} className="mr-1" /> Send</Button>
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 rounded-lg ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => handleDownloadPdf(q.id, q)}><Download size={14} /></Button>
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 rounded-lg ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => openEdit(q)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400/60 hover:bg-red-500/10 h-8 w-8 p-0 rounded-lg" onClick={() => handleDelete(q)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
      })()}

      {/* Send Dialog */}
      <Dialog open={!!sendDialogId} onOpenChange={() => setSendDialogId(null)}>
        <DialogContent className={`max-w-sm ${dialogCls}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Send Quotation</DialogTitle><DialogDescription className={isDark ? 'text-white/40' : 'text-gray-500'}>Send via email and/or WhatsApp</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3"><input type="checkbox" checked={sendOpts.email} onChange={e => setSendOpts(s => ({ ...s, email: e.target.checked }))} className="rounded" /><Label className={`${isDark ? 'text-white/70' : 'text-gray-600'} flex items-center gap-1.5`}><Mail size={14} /> Email</Label></div>
            {sendOpts.email && <Input value={sendOpts.customer_email} onChange={e => setSendOpts(s => ({ ...s, customer_email: e.target.value }))} placeholder="Customer Email" className={inputCls} />}
            <div className="flex items-center gap-3"><input type="checkbox" checked={sendOpts.whatsapp} onChange={e => setSendOpts(s => ({ ...s, whatsapp: e.target.checked }))} className="rounded" /><Label className={`${isDark ? 'text-white/70' : 'text-gray-600'} flex items-center gap-1.5`}><MessageSquare size={14} /> WhatsApp</Label></div>
            {sendOpts.whatsapp && <Input value={sendOpts.customer_phone} onChange={e => setSendOpts(s => ({ ...s, customer_phone: e.target.value }))} placeholder="Phone (+91...)" className={inputCls} />}
            <Button onClick={handleSendQuotation} className="w-full bg-blue-600 hover:bg-blue-700"><Send size={14} className="mr-2" /> Send Quotation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Quotation Dialog */}
      <Dialog open={!!viewQuotation} onOpenChange={() => setViewQuotation(null)}>
        <DialogContent className={`max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto ${dialogCls}`} data-testid="quotation-view-dialog">
          {loadingView && <div className={`py-8 text-center text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Loading…</div>}
          {viewQuotation && !loadingView && (() => {
            const q = viewQuotation;
            const isProject = q.quotation_type === 'project';
            const allItems = isProject
              ? (q.operations || []).flatMap(op => (op.items || []).map(it => ({ ...it, _op: op.op_number || op.operation_name })))
              : (q.items || []);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                    {q.quotation_number} — {q.customer_name}
                  </DialogTitle>
                  <DialogDescription className={isDark ? 'text-white/40' : 'text-gray-500'}>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize border ${STATUS_COLORS[q.status]}`}>{q.status}</span>
                    {isProject && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">Project</span>}
                    {q.firm_name && <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-100 text-gray-500'}`}>{q.firm_name}</span>}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                  {/* Header info grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Customer:</span> {q.customer_name}</div>
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Company:</span> {q.customer_company || '-'}</div>
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Email:</span> {q.customer_email || '-'}</div>
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Phone:</span> {q.customer_phone || '-'}</div>
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Created:</span> {q.created_at?.split('T')[0] || '-'}</div>
                    <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>By:</span> {q.contact_person_name || q.sales_engineer_name || '-'}</div>
                    {isProject && q.component_name && <div className="col-span-2 sm:col-span-3"><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Component:</span> {q.component_name}</div>}
                  </div>

                  {/* Line items */}
                  <div>
                    <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Items ({allItems.length})</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                            <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>#</th>
                            {isProject && <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Op</th>}
                            <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Product</th>
                            <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Make / HSN</th>
                            <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty</th>
                            <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Rate</th>
                            <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allItems.map((it, idx) => {
                            const rate = (it.price_type || 'net') === 'list'
                              ? (it.list_price || 0) * (1 - (it.discount_percent || 0) / 100)
                              : (it.net_price || it.rate || 0);
                            const amt = (it.amount != null && it.amount !== 0) ? it.amount : (it.quantity || 0) * rate;
                            return (
                              <tr key={`${it._op || ''}-${idx}`} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <td className={`py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{idx + 1}</td>
                                {isProject && <td className={`py-2 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{it._op || '-'}</td>}
                                <td className={`py-2 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  <div>{it.product_name}</div>
                                  {it.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{it.description}</div>}
                                </td>
                                <td className={`py-2 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{it.make || '-'} {it.hsn_code ? `/ ${it.hsn_code}` : ''}</td>
                                <td className={`py-2 px-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{it.quantity} {it.unit || ''}</td>
                                <td className={`py-2 px-2 text-right ${isDark ? 'text-white/80' : 'text-gray-700'}`}>₹{rate.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                <td className={`py-2 px-2 text-right font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{amt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'} space-y-1 text-sm`}>
                    <div className="flex justify-between"><span className={isDark ? 'text-white/60' : 'text-gray-600'}>Subtotal</span><span className={isDark ? 'text-white' : 'text-gray-900'}>₹{(q.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                    {(q.cgst_amount > 0) && <div className="flex justify-between"><span className={isDark ? 'text-white/60' : 'text-gray-600'}>CGST ({q.cgst_rate}%)</span><span className={isDark ? 'text-white' : 'text-gray-900'}>₹{q.cgst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                    {(q.sgst_amount > 0) && <div className="flex justify-between"><span className={isDark ? 'text-white/60' : 'text-gray-600'}>SGST ({q.sgst_rate}%)</span><span className={isDark ? 'text-white' : 'text-gray-900'}>₹{q.sgst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                    {(q.igst_amount > 0) && <div className="flex justify-between"><span className={isDark ? 'text-white/60' : 'text-gray-600'}>IGST ({q.igst_rate}%)</span><span className={isDark ? 'text-white' : 'text-gray-900'}>₹{q.igst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                    <div className={`flex justify-between pt-2 border-t font-bold text-base ${isDark ? 'border-white/10 text-white' : 'border-gray-200 text-gray-900'}`}><span>Total</span><span>₹{(q.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  </div>

                  {q.notes && (
                    <div>
                      <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Notes</p>
                      <p className={`text-xs whitespace-pre-wrap ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{q.notes}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button size="sm" variant="ghost" className={`${isDark ? 'text-white/60 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleDownloadPdf(q.id, q)} data-testid="quotation-view-download">
                      <Download size={14} className="mr-2" /> Download PDF
                    </Button>
                    <Button size="sm" variant="ghost" className={`${isDark ? 'text-white/60 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => { setViewQuotation(null); openEdit(q); }} data-testid="quotation-view-edit">
                      <Pencil size={14} className="mr-2" /> Edit
                    </Button>
                    <Button size="sm" onClick={() => setViewQuotation(null)} data-testid="quotation-view-close">Close</Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog */}
      <Dialog open={!!deleteReqDialog} onOpenChange={() => setDeleteReqDialog(null)}>
        <DialogContent className={`max-w-md ${dialogCls}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              <AlertTriangle size={18} className="inline mr-2 text-amber-400" />
              Request Admin Approval
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/50' : 'text-gray-500'}>
              This quotation is older than 48 hours. Only admin can delete it. Submit a request below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-600'}`}>
              <p><strong>Quotation:</strong> {deleteReqDialog?.quotation_number}</p>
              <p><strong>Customer:</strong> {deleteReqDialog?.customer_name}</p>
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : 'text-gray-600'}>Reason for deletion (optional)</Label>
              <Textarea
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                placeholder="Why should this quotation be deleted?"
                className={`mt-1 ${inputCls}`}
                rows={3}
              />
            </div>
            <Button onClick={handleDeleteRequest} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="submit-delete-request">
              <Send size={14} className="mr-2" /> Send Delete Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: Pending Delete Requests */}
      {isAdmin && deleteRequests.length > 0 && (
        <Card className={`${cardBorderCls} mt-4`} data-testid="delete-requests-section">
          <CardContent className="p-4">
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              <AlertTriangle size={16} /> Pending Delete Requests ({deleteRequests.length})
            </h3>
            <div className="space-y-2">
              {deleteRequests.map(req => (
                <div key={req.id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className="space-y-0.5 flex-1">
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {req.quotation_number} - {req.customer_name}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                      Requested by: {req.requested_by_name} {req.reason && `| Reason: ${req.reason}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 px-3" onClick={() => handleApproveDelete(req.id)}>
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className={`h-8 px-3 ${btnOutlineCls}`} onClick={() => handleRejectDelete(req.id)}>
                      <XIcon size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (o) { setDialogOpen(true); } else { requestCloseDialog(); } }}>
        <DialogContent className={`max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 ${dialogCls}`}>
          <div className={`sticky top-0 z-10 border-b px-6 py-4 ${isDark ? 'bg-[#0c1222] border-white/10' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`text-lg flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editing ? 'Edit' : 'New'} {form.quotation_type === 'project' ? 'Project' : 'General'} Quotation
                {form.quotation_type === 'project' && <span className="text-xs bg-indigo-500/10 text-indigo-400 px-2.5 py-1 rounded-full border border-indigo-500/20">Project</span>}
              </DialogTitle>
              <DialogDescription className={isDark ? 'text-white/40' : 'text-gray-500'}>Fill in the quotation details</DialogDescription>
            </DialogHeader>
          </div>

          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label className={`${labelCls} mb-1.5 block`}>Firm *</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.firm_id} onChange={e => setForm({ ...form, firm_id: e.target.value })} required data-testid="q-firm-select">
                  <option value="">Select Firm</option>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select></div>
              <div><Label className={`${labelCls} mb-1.5 block`}>Status</Label>
                <select className={`w-full h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select></div>
              <div><Label className={`${labelCls} mb-1.5 block`}>Valid Until</Label><Input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
              <div><Label className={`${labelCls} mb-1.5 block`}>Contact Person</Label><Input value={form.contact_person_name} onChange={e => setForm({ ...form, contact_person_name: e.target.value })} placeholder="Name" className={`${inputCls} h-10 rounded-lg`} data-testid="q-contact-name" /></div>
            </div>

            <div className={`${sectionCls} rounded-xl p-5`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Contact Person Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><Label className={`${labelSecCls} mb-1 block`}>Designation</Label><Input value={form.contact_person_designation} onChange={e => setForm({ ...form, contact_person_designation: e.target.value })} placeholder="e.g. Sales Manager" className={`${inputCls} h-10 rounded-lg`} data-testid="q-contact-designation" /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Mobile Number</Label><Input value={form.contact_person_mobile} onChange={e => setForm({ ...form, contact_person_mobile: e.target.value })} placeholder="+91..." className={`${inputCls} h-10 rounded-lg`} data-testid="q-contact-mobile" /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Email</Label><Input value={form.contact_person_email} onChange={e => setForm({ ...form, contact_person_email: e.target.value })} placeholder="email@company.com" className={`${inputCls} h-10 rounded-lg`} /></div>
              </div>
            </div>

            <div className={`${sectionCls} rounded-xl p-5`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Customer Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><Label className={`${labelSecCls} mb-1 block`}>Customer Name *</Label><Input value={form.customer_name} onChange={e => { setForm({ ...form, customer_name: e.target.value }); selectCustomer(e.target.value); }} required className={`${inputCls} h-10 rounded-lg`} list="cust-list" data-testid="q-customer-name" /><datalist id="cust-list">{myCustomers.map((c) => <option key={c.name} value={c.name} />)}</datalist></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Company</Label><Input value={form.customer_company} onChange={e => setForm({ ...form, customer_company: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>GST</Label><Input value={form.customer_gst} onChange={e => setForm({ ...form, customer_gst: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Phone</Label><Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Email</Label><Input value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
                <div><Label className={`${labelSecCls} mb-1 block`}>Address</Label><Input value={form.customer_address} onChange={e => setForm({ ...form, customer_address: e.target.value })} className={`${inputCls} h-10 rounded-lg`} /></div>
              </div>
            </div>

            {form.quotation_type === 'project' && (
              <>
                <div className={`rounded-xl p-5 ${isDark ? 'bg-indigo-500/[0.04] border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200'}`}>
                  <h3 className="text-xs font-semibold text-indigo-400/70 uppercase tracking-wider mb-4 flex items-center gap-2"><Settings2 size={14} /> Component Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className={`${labelSecCls} mb-1 block`}>Component Name *</Label><Input value={form.component_name} onChange={e => setForm({ ...form, component_name: e.target.value })} placeholder="e.g., Shaft Assembly, Gear Housing" className={`${inputCls} h-10 rounded-lg`} data-testid="component-name" /></div>
                    <div><Label className={`${labelSecCls} mb-1 block`}>Component Material</Label><Input value={form.component_material} onChange={e => setForm({ ...form, component_material: e.target.value })} placeholder="e.g., EN24 Steel, Cast Iron" className={`${inputCls} h-10 rounded-lg`} data-testid="component-material" /></div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}><Layers size={14} /> Operations</h3>
                    <Button type="button" size="sm" variant="ghost" onClick={addOperation} className="text-indigo-400 hover:bg-indigo-500/10 h-8 text-xs rounded-lg" data-testid="add-operation-btn"><Plus size={14} className="mr-1" /> Add Operation</Button>
                  </div>

                  {form.operations.map((op, oi) => (
                    <div key={oi} className={`${sectionCls} rounded-xl overflow-hidden`} data-testid={`operation-${oi}`}>
                      <div className={`px-4 md:px-5 py-3 border-b ${isDark ? 'bg-indigo-500/[0.05] border-indigo-500/10' : 'bg-indigo-50 border-indigo-200'}`}>
                        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                          <div className="w-20"><Input value={op.op_number} onChange={e => updateOperation(oi, 'op_number', e.target.value)} placeholder="OP10" className={`${inputCls} h-9 rounded-lg text-sm font-mono font-bold text-center ${isDark ? '!text-indigo-300' : '!text-indigo-600'}`} /></div>
                          <div className="flex-1 min-w-[150px]"><Input value={op.operation_name} onChange={e => updateOperation(oi, 'operation_name', e.target.value)} placeholder="Operation Name (e.g., Rough Turning)" className={`${inputCls} h-9 rounded-lg text-sm`} /></div>
                          <div className="w-full sm:w-40"><Input value={op.machine_name} onChange={e => updateOperation(oi, 'machine_name', e.target.value)} placeholder="Machine (e.g., CNC Lathe)" className={`${inputCls} h-9 rounded-lg text-sm`} /></div>
                          {form.operations.length > 1 && <button type="button" onClick={() => removeOperation(oi)} className="text-red-400/50 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10"><XIcon size={14} /></button>}
                        </div>
                      </div>
                      <ColHeaders isDark={isDark} />
                      <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                        {op.items.map((item, ii) => (
                          <ItemRow
                            key={ii} item={item} index={ii}
                            listId={`op${oi}`}
                            products={myProducts}
                            isDark={isDark}
                            onUpdate={(idx, field, val) => {
                              if (field === 'product_name') onProductSelectOp(oi, idx, val);
                              updateOpItem(oi, idx, field, val);
                            }}
                            onRemove={(idx) => removeOpItem(oi, idx)}
                            canRemove={op.items.length > 1}
                          />
                        ))}
                      </div>
                      <div className={`px-4 py-2 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                        <Button type="button" size="sm" variant="ghost" onClick={() => addOpItem(oi)} className="text-blue-400 hover:bg-blue-500/10 h-7 text-xs rounded-lg"><Plus size={12} className="mr-1" /> Add Item</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {form.quotation_type === 'general' && (
              <div className={`${sectionCls} rounded-xl overflow-hidden`}>
                <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-white/8' : 'border-gray-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Line Items</h3>
                  <Button type="button" size="sm" variant="ghost" onClick={addItem} className="text-blue-400 hover:bg-blue-500/10 h-8 text-xs rounded-lg"><Plus size={14} className="mr-1" /> Add Item</Button>
                </div>
                <ColHeaders isDark={isDark} />
                <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {form.items.map((item, i) => (
                    <ItemRow
                      key={i} item={item} index={i} listId="gen"
                      products={myProducts}
                      isDark={isDark}
                      onUpdate={(idx, field, val) => {
                        if (field === 'product_name') {
                          const items = [...form.items];
                          items[idx] = selectProduct(items[idx], val);
                          setForm(f => ({ ...f, items }));
                        }
                        updateItem(idx, field, val);
                      }}
                      onRemove={removeItem}
                      canRemove={form.items.length > 1}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
              <div><Label className={`${labelSecCls} mb-1 block`}>CGST %</Label>
                <input type="text" inputMode="decimal" value={form.cgst_rate} onChange={e => setForm({ ...form, cgst_rate: parseFloat(e.target.value) || 0 })} className={`w-full h-10 rounded-lg text-sm px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/40 border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} /></div>
              <div><Label className={`${labelSecCls} mb-1 block`}>SGST %</Label>
                <input type="text" inputMode="decimal" value={form.sgst_rate} onChange={e => setForm({ ...form, sgst_rate: parseFloat(e.target.value) || 0 })} className={`w-full h-10 rounded-lg text-sm px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/40 border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} /></div>
              <div><Label className={`${labelSecCls} mb-1 block`}>IGST %</Label>
                <input type="text" inputMode="decimal" value={form.igst_rate} onChange={e => setForm({ ...form, igst_rate: parseFloat(e.target.value) || 0 })} className={`w-full h-10 rounded-lg text-sm px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/40 border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} /></div>
              <div className={`rounded-xl p-4 text-right border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Subtotal: Rs.{Math.round(computed.subtotal).toLocaleString('en-IN')}</p>
                {computed.cgst_amount > 0 && <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>CGST: Rs.{Math.round(computed.cgst_amount).toLocaleString('en-IN')}</p>}
                {computed.sgst_amount > 0 && <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>SGST: Rs.{Math.round(computed.sgst_amount).toLocaleString('en-IN')}</p>}
                {computed.igst_amount > 0 && <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>IGST: Rs.{Math.round(computed.igst_amount).toLocaleString('en-IN')}</p>}
                <p className={`text-xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Rs.{Math.round(computed.total).toLocaleString('en-IN')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className={`${labelSecCls} mb-1 block`}>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className={`${inputCls} rounded-lg resize-none`} /></div>
              <div><Label className={`${labelSecCls} mb-1 block`}>Terms & Conditions</Label><Textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} rows={3} className={`${inputCls} rounded-lg resize-none`} /></div>
            </div>

            <div className={`flex gap-3 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
              <Button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700 h-11 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" data-testid="q-submit-btn">{submitting ? 'Saving...' : editing ? 'Update' : 'Create'} {submitting ? '' : 'Quotation'}</Button>
              <Button type="button" variant="outline" onClick={requestCloseDialog} className={`h-11 px-6 rounded-lg ${btnOutlineCls}`}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quotations;
